/**
 * Canonicalises Swedish personnummer to the 12-digit no-separator form (YYYYMMDDNNNN).
 *
 * Mirrors the PHP `App\Dermicus\Patients\SwedishSsn` value object so the frontend can
 * normalise input before the request is sent. The backend still re-normalises on the
 * server as defence in depth — keep both implementations in sync if you change either.
 *
 * Accepted input formats:
 *   YYYYMMDDNNNN          long no separator
 *   YYYYMMDD-NNNN         long with dash
 *   YYMMDDNNNN            short no separator
 *   YYMMDD-NNNN           short with dash
 *   YYMMDD+NNNN           short with '+' (person is 100+ years old)
 */
export const SSN_TYPE_SWEDISH = "SSN_SE" as const;

export function isSwedishSsnType(ssnType: string | null | undefined): boolean {
  return ssnType === SSN_TYPE_SWEDISH;
}

export function normaliseSwedishSsn(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed === "") return null;

  const hasPlus = trimmed.includes("+");
  const digits = trimmed.replace(/\D/g, "");

  if (digits.length === 12) return digits;
  if (digits.length !== 10) return null;

  const yy = parseInt(digits.slice(0, 2), 10);
  const year = pickBirthYear(yy, hasPlus);
  if (year === null) return null;

  return `${year}${digits.slice(2)}`;
}

/**
 * Resolves a 2-digit year to a 4-digit year by picking the most recent century where
 * the result satisfies the separator constraint:
 *   '+'  → person is currently 100 or older
 *   '-'  → person is currently younger than 100
 */
function pickBirthYear(yy: number, hasPlus: boolean): number | null {
  const now = new Date().getFullYear();
  const currentCentury = Math.floor(now / 100);

  for (let century = currentCentury; century >= currentCentury - 2; century--) {
    const year = century * 100 + yy;
    if (year > now) continue;
    const age = now - year;
    if (hasPlus ? age >= 100 : age < 100) return year;
  }

  return null;
}
